import type { App } from '../../app.js';
import { h, button, toast } from '../dom.js';
import { allTrackDefs, RaceMode, BotDifficulty } from '@voidline/shared';

/**
 * Multiplayer lobby: roster, ready states, chat, host settings, track voting.
 * Rebuilt in place on every roomState update (updateLobbyScreen).
 */

let refs: {
  roster?: HTMLElement;
  chatLog?: HTMLElement;
  settingsBox?: HTMLElement;
  readyBtn?: HTMLButtonElement;
  startBtn?: HTMLButtonElement;
  codeEl?: HTMLElement;
  statusEl?: HTMLElement;
} = {};
const mutedIds = new Set<string>();

export function buildLobbyScreen(app: App): HTMLElement {
  refs = {};
  const chatInput = h('input', {
    type: 'text',
    maxlength: '240',
    placeholder: 'Say something…',
    style: 'flex: 1; font-size: 12px;',
  }) as HTMLInputElement;
  chatInput.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && chatInput.value.trim()) {
      app.conn?.send({ type: 'chat', text: chatInput.value.trim() });
      chatInput.value = '';
    }
  });

  refs.roster = h('div', { class: 'col', style: 'gap: 6px; min-width: 300px;' });
  refs.chatLog = h('div', {
    class: 'col mono',
    style: 'gap: 3px; height: 160px; overflow-y: auto; font-size: 11px; background: rgba(6,10,16,0.6); padding: 10px; border: 1px solid var(--line);',
  });
  refs.settingsBox = h('div', { class: 'col', style: 'gap: 10px;' });
  refs.codeEl = h('span', { class: 'mono', style: 'color: var(--accent); font-size: 20px; letter-spacing: 0.3em;' });
  refs.statusEl = h('div', { class: 'subtle', style: 'text-transform:none; font-size: 12px;' });

  refs.readyBtn = button('READY', () => {
    const me = app.roomState?.players.find((p) => p.id === app.roomState?.yourPlayerId);
    app.conn?.send({ type: 'setReady', ready: !me?.ready });
    app.audio.play('ui_confirm');
  }, 'primary focusable') as HTMLButtonElement;

  refs.startBtn = button('START RACE', () => {
    app.conn?.send({ type: 'startRace' });
    app.audio.play('ui_confirm');
  }, 'primary focusable') as HTMLButtonElement;

  const screen = h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel', style: 'width: min(880px, 94vw); max-height: 90vh; overflow-y: auto;' },
      h(
        'div',
        { class: 'row', style: 'justify-content: space-between; margin-bottom: 14px;' },
        h('div', { class: 'row', style: 'gap: 18px; align-items: baseline;' },
          h('div', { class: 'h1', text: 'LOBBY' }),
          h('span', { class: 'h2', text: 'CODE' }),
          refs.codeEl,
          button('COPY', () => {
            navigator.clipboard?.writeText(app.roomState?.code ?? '').then(
              () => toast('Room code copied.'),
              () => toast('Could not copy — code is shown above.', true)
            );
          }, 'small focusable')
        ),
        button('LEAVE', () => {
          app.audio.play('ui_back');
          app.leaveRoom();
        }, 'small danger focusable')
      ),
      refs.statusEl,
      h(
        'div',
        { class: 'row', style: 'gap: 30px; align-items: flex-start; margin-top: 12px;' },
        h('div', { class: 'col', style: 'gap: 12px;' },
          h('div', { class: 'h2', text: 'PILOTS' }),
          refs.roster,
          h('div', { class: 'row', style: 'gap: 10px; margin-top: 8px;' }, refs.readyBtn, refs.startBtn)
        ),
        h('div', { class: 'col grow', style: 'gap: 12px; min-width: 320px;' },
          refs.settingsBox,
          h('div', { class: 'h2', text: 'COMMS' }),
          refs.chatLog,
          h('div', { class: 'row' }, chatInput)
        )
      )
    )
  );

  updateLobbyScreen(app);
  return screen;
}

export function updateLobbyScreen(app: App, extra?: { chat?: { from: string; text: string } }): void {
  const state = app.roomState;
  if (!state) return;

  if (extra?.chat && refs.chatLog) {
    const fromId = app.roomState?.players.find((p) => p.name === extra.chat!.from)?.id ?? '';
    if (!mutedIds.has(fromId)) {
      refs.chatLog.appendChild(
        h('div', {}, h('span', { style: 'color: var(--accent);', text: extra.chat.from + ' ' }), h('span', { text: extra.chat.text }))
      );
      refs.chatLog.scrollTop = refs.chatLog.scrollHeight;
      while (refs.chatLog.children.length > 60) refs.chatLog.firstChild?.remove();
    }
    return;
  }

  if (!refs.roster) return;
  const isHost = state.hostId === state.yourPlayerId;
  const me = state.players.find((p) => p.id === state.yourPlayerId);

  refs.codeEl!.textContent = state.code;
  refs.statusEl!.textContent = state.isPrivate
    ? 'Private room — share the code with friends.'
    : 'Public room — racers may join at any time. Everyone ready = race starts.';

  // roster
  refs.roster.innerHTML = '';
  for (const p of state.players) {
    const isMe = p.id === state.yourPlayerId;
    refs.roster.appendChild(
      h(
        'div',
        {
          class: 'row',
          style: `justify-content: space-between; padding: 7px 12px; border: 1px solid var(--line); gap: 14px;
                  ${isMe ? 'border-color: var(--accent);' : ''} ${p.connected ? '' : 'opacity: 0.5;'}`,
        },
        h('div', {},
          h('span', { text: p.name + (isMe ? ' (you)' : '') }),
          h('div', {
            class: 'subtle',
            style: 'font-size: 10px; text-transform: none;',
            text: `${p.vehicleId}${p.isSpectator ? ' · spectating' : ''}${p.connected ? '' : ' · reconnecting…'}${p.id === state.hostId ? ' · host' : ''}${p.ping ? ` · ${p.ping}ms` : ''}`,
          })
        ),
        h('div', { class: 'row', style: 'gap: 8px;' },
          !isMe
            ? button(mutedIds.has(p.id) ? 'UNMUTE' : 'MUTE', () => {
                if (mutedIds.has(p.id)) mutedIds.delete(p.id);
                else mutedIds.add(p.id);
                updateLobbyScreen(app);
              }, 'small focusable')
            : null,
          h('span', {
            style: `font-size: 11px; letter-spacing: 0.2em; color: ${p.ready ? 'var(--ok)' : 'var(--text-dim)'};`,
            text: p.ready ? 'READY' : 'WAITING',
          })
        )
      )
    );
  }

  // ready/start buttons
  refs.readyBtn!.textContent = me?.ready ? 'UNREADY' : 'READY';
  refs.startBtn!.style.display = isHost && state.phase === 'lobby' ? '' : 'none';

  // settings (host editable; others see values + can vote)
  const box = refs.settingsBox!;
  box.innerHTML = '';
  const s = state.settings;
  const tracks = allTrackDefs();

  const settingRow = (label: string, el: HTMLElement) =>
    h('div', { class: 'row', style: 'justify-content: space-between; gap: 12px; align-items: center;' },
      h('span', { class: 'subtle', style: 'font-size: 12px;', text: label }), el);

  if (isHost) {
    const trackSel = h('select', {}) as HTMLSelectElement;
    for (const t of tracks) {
      const opt = h('option', { value: t.id, text: `${t.name}` });
      if (t.id === s.trackId) opt.setAttribute('selected', '');
      trackSel.appendChild(opt);
    }
    trackSel.addEventListener('change', () =>
      app.conn?.send({ type: 'setSettings', settings: { trackId: trackSel.value, trackSeed: null } })
    );

    const modeSel = h('select', {}) as HTMLSelectElement;
    for (const m of ['race', 'sprint', 'elimination'] as RaceMode[]) {
      const opt = h('option', { value: m, text: m.toUpperCase() });
      if (m === s.mode) opt.setAttribute('selected', '');
      modeSel.appendChild(opt);
    }
    modeSel.addEventListener('change', () =>
      app.conn?.send({ type: 'setSettings', settings: { mode: modeSel.value as RaceMode } })
    );

    const lapsSel = h('select', {}) as HTMLSelectElement;
    for (const n of [1, 2, 3, 5, 7]) {
      const opt = h('option', { value: String(n), text: `${n} LAP${n > 1 ? 'S' : ''}` });
      if (n === s.laps) opt.setAttribute('selected', '');
      lapsSel.appendChild(opt);
    }
    lapsSel.addEventListener('change', () =>
      app.conn?.send({ type: 'setSettings', settings: { laps: Number(lapsSel.value) } })
    );

    const botsSel = h('select', {}) as HTMLSelectElement;
    for (const n of [0, 1, 2, 3, 4, 5, 6, 7]) {
      const opt = h('option', { value: String(n), text: `${n} BOTS` });
      if (n === s.botCount) opt.setAttribute('selected', '');
      botsSel.appendChild(opt);
    }
    botsSel.addEventListener('change', () =>
      app.conn?.send({ type: 'setSettings', settings: { botCount: Number(botsSel.value) } })
    );

    const diffSel = h('select', {}) as HTMLSelectElement;
    for (const d of ['cruise', 'standard', 'hostile', 'nemesis'] as BotDifficulty[]) {
      const opt = h('option', { value: d, text: d.toUpperCase() });
      if (d === s.botDifficulty) opt.setAttribute('selected', '');
      diffSel.appendChild(opt);
    }
    diffSel.addEventListener('change', () =>
      app.conn?.send({ type: 'setSettings', settings: { botDifficulty: diffSel.value as BotDifficulty } })
    );

    box.append(
      h('div', { class: 'h2', text: 'RACE SETTINGS (HOST)' }),
      settingRow('Track', trackSel),
      settingRow('Mode', modeSel),
      settingRow('Laps', lapsSel),
      settingRow('Bot fill', botsSel),
      settingRow('Bot skill', diffSel)
    );
  } else {
    const trackName = tracks.find((t) => t.id === s.trackId)?.name ?? s.trackId;
    box.append(
      h('div', { class: 'h2', text: 'RACE SETTINGS' }),
      h('div', {
        class: 'subtle',
        style: 'text-transform: none; font-size: 12px;',
        text: `${trackName} · ${s.mode.toUpperCase()} · ${s.laps} laps · ${s.botCount} bots (${s.botDifficulty})`,
      })
    );
  }

  // track voting
  const voteBox = h('div', { class: 'row wrap', style: 'gap: 6px;' });
  for (const t of tracks.slice(0, 10)) {
    const votes = state.votes[t.id] ?? 0;
    voteBox.appendChild(
      button(`${t.name}${votes ? ` (${votes})` : ''}`, () => {
        app.conn?.send({ type: 'voteTrack', trackId: t.id });
        app.audio.play('ui_move');
      }, 'small focusable')
    );
  }
  box.append(h('div', { class: 'h2', text: 'VOTE NEXT TRACK', style: 'margin-top: 6px;' }), voteBox);

  // loadout quick-switch
  box.append(
    h('div', { class: 'h2', text: 'YOUR CRAFT', style: 'margin-top: 6px;' }),
    h('div', { class: 'row', style: 'gap: 10px; align-items: center;' },
      h('span', { class: 'subtle', style: 'font-size: 12px; text-transform: none;', text: me?.vehicleId ?? '' }),
      button('CHANGE IN GARAGE', () => {
        toast('Pick a craft, then return to the lobby from Multiplayer.', false, 4200);
        app.show('garage');
      }, 'small focusable'))
  );
}
